import { Controller } from '@hotwired/stimulus';
import { updateManualAnalysisProgress } from '../services/manual-analysis-progress';
import {csrfHeaders} from '../services/form.services';

type CropBox = { x: number; y: number; width: number; height: number };
type CropInteraction =
    | { mode: 'move'; pointerId: number; startClientX: number; startClientY: number; startBox: CropBox }
    | { mode: 'resize'; pointerId: number; edge: string; startClientX: number; startClientY: number; startBox: CropBox };

const MAX_SOURCE_IMAGE_BYTES = 25 * 1024 * 1024;
const MAX_UPLOAD_IMAGE_DIMENSION = 1600;
const UPLOAD_JPEG_QUALITY = 0.82;
const ANALYSIS_STATUS_POLL_INTERVAL_MS = 1200;
export class AnalysisFormController extends Controller {
    declare readonly formTarget: HTMLFormElement;
    declare readonly uploadContentTarget: HTMLElement;
    declare readonly submitButtonTarget: HTMLButtonElement;
    declare readonly hasSubmitButtonTarget: boolean;
    declare readonly queueButtonTarget: HTMLButtonElement;
    declare readonly hasQueueButtonTarget: boolean;
    declare readonly additionalInfoTarget: HTMLTextAreaElement;
    declare readonly hasAdditionalInfoTarget: boolean;
    declare readonly recordButtonTarget: HTMLButtonElement;
    declare readonly hasRecordButtonTarget: boolean;
    declare readonly recordStatusTarget: HTMLElement;
    declare readonly hasRecordStatusTarget: boolean;
    declare readonly fileInputTarget: HTMLInputElement;
    declare readonly hasFileInputTarget: boolean
    declare readonly captureButtonTarget: HTMLButtonElement;
    declare readonly hasCaptureButtonTarget: boolean;
    declare readonly controlButtonsTarget: HTMLElement;
    declare readonly hasControlButtonsTarget: boolean;
    declare readonly photoGuidanceTarget: HTMLElement;
    declare readonly hasPhotoGuidanceTarget: boolean;
    declare readonly manualPanelTarget: HTMLElement;
    declare readonly hasManualPanelTarget: boolean;
    declare readonly manualSubmitTarget: HTMLElement;
    declare readonly hasManualSubmitTarget: boolean;
    declare readonly manualPromptTarget: HTMLElement;
    declare readonly hasManualPromptTarget: boolean;
    declare readonly mealTitleTarget: HTMLElement;
    declare readonly hasMealTitleTarget: boolean;
    declare readonly uploadSectionTarget: HTMLElement;
    declare readonly hasUploadSectionTarget: boolean;
    declare readonly detailsPanelTarget: HTMLElement;
    declare readonly hasDetailsPanelTarget: boolean;
    declare readonly detailsCardTarget: HTMLElement;
    declare readonly hasDetailsCardTarget: boolean;
    declare readonly secondaryFileInputTarget: HTMLInputElement;
    declare readonly hasSecondaryFileInputTarget: boolean;
    declare readonly secondaryUploadButtonTarget: HTMLElement;
    declare readonly hasSecondaryUploadButtonTarget: boolean;
    declare readonly secondaryCaptureButtonTarget: HTMLButtonElement;
    declare readonly hasSecondaryCaptureButtonTarget: boolean;
    declare readonly referenceButtonTargets: HTMLElement[];
    declare readonly referenceListTarget: HTMLUListElement;
    declare readonly hasReferenceListTarget: boolean;
    declare readonly referenceContextTarget: HTMLElement;
    declare readonly hasReferenceContextTarget: boolean;
    declare readonly reanalyseModeValue: boolean;

    static targets = [
        "form",
        "uploadContent",
        "submitButton",
        "queueButton",
        "additionalInfo",
        "recordButton",
        "recordStatus",
        "fileInput",
        "captureButton",
        "controlButtons",
        "photoGuidance",
        "manualPanel",
        "manualSubmit",
        "manualPrompt",
        "mealTitle",
        "uploadSection",
        "detailsPanel",
        "detailsCard",
        "secondaryFileInput",
        "secondaryUploadButton",
        "secondaryCaptureButton",
        "referenceButton",
        "referenceList",
        "referenceContext"
    ];

    static values = {
        reanalyseMode: { type: Boolean, default: false }
    };

    private selectedFile: File | null = null;
    private secondarySelectedFile: File | null = null;
    private stream: MediaStream | null = null;
    private video: HTMLVideoElement | null = null;
    private canvas: HTMLCanvasElement | null = null;
    private isCameraMode = false;
    private cameraSupported = true;
    private mediaRecorder: MediaRecorder | null = null;
    private audioStream: MediaStream | null = null;
    private audioChunks: Blob[] = [];
    private isRecording = false;
    private isTranscribing = false;
    private speechToTextSupported = true;
    private previewObjectUrl: string | null = null;
    private secondaryPreviewObjectUrl: string | null = null;
    private activePhotoIndex = 0;
    private cameraCapturePhotoIndex: 0 | 1 = 0;
    private photoSwipeStartClientX: number | null = null;
    private cropBox: CropBox = { x: 8, y: 8, width: 84, height: 84 };
    private cropInteraction: CropInteraction | null = null;
    private isCropMode = false;
    private analysisStatusPollTimer: number | null = null;
    private analysisStatusAbortController: AbortController | null = null;
    private activeAnalysisRequestId: string | null = null;
    private queueActive = false;
    private addingQueueImage = false;
    private queueSuccessTimer: number | null = null;
    private referenceFiles: File[][] = [[], []];
    private defaultUploadContent = '';
    connect() {
        this.defaultUploadContent = this.uploadContentTarget.innerHTML;
        this.cameraSupported = this.isCameraSupported();
        this.speechToTextSupported = this.isSpeechToTextSupported();
        this.updateSubmitButtonState();
        this.updateLayoutState(!!this.selectedFile);
        this.updateControlButtons('default');
        this.updateSecondaryUploadButtonState();
        this.updateReferenceControls();
        this.updateSpeechToTextUi();
        this.formTarget.addEventListener('turbo:submit-end', this.handleAnalysisSubmitEnd);
        if (new URLSearchParams(window.location.search).get('mode') === 'manual') {
            this.showManualMode();
        }
    }

    showManualMode = (event?: Event): void => {
        event?.preventDefault();
        this.stopCamera();
        if (this.hasPhotoGuidanceTarget) this.photoGuidanceTarget.classList.add('hidden');
        if (this.hasManualPanelTarget) this.manualPanelTarget.classList.remove('hidden');
        if (this.hasManualSubmitTarget) this.manualSubmitTarget.classList.remove('hidden');
        if (this.hasControlButtonsTarget) this.controlButtonsTarget.classList.add('hidden');
        if (this.hasManualPromptTarget) this.manualPromptTarget.classList.add('hidden');
        window.requestAnimationFrame(() => {
            document.querySelector<HTMLTextAreaElement>('#inline-manual-meal-description')?.focus();
        });
    };

    showPhotoMode = (event?: Event): void => {
        event?.preventDefault();
        if (this.hasManualPanelTarget) this.manualPanelTarget.classList.add('hidden');
        if (this.hasManualSubmitTarget) this.manualSubmitTarget.classList.add('hidden');
        if (this.hasPhotoGuidanceTarget) this.photoGuidanceTarget.classList.remove('hidden');
        if (this.hasControlButtonsTarget) this.controlButtonsTarget.classList.toggle('hidden', Boolean(this.selectedFile));
        if (this.hasManualPromptTarget) this.manualPromptTarget.classList.remove('hidden');
    };

    disconnect() {
        if (this.queueSuccessTimer !== null) window.clearTimeout(this.queueSuccessTimer);
        this.stopAnalysisStatusPolling();
        this.formTarget.removeEventListener('turbo:submit-end', this.handleAnalysisSubmitEnd);
        this.stopCamera();
        this.stopSpeechCapture();
        this.revokePreviewObjectUrl();
    }

    // Stimulus action for file input change
    fileSelected = async () => {
        const file = this.fileInputTarget.files?.[0];

        if (file && this.validateImage(file)) {
            const normalizedFile = await this.normalizeUploadFile(file);
            if (!normalizedFile) {
                this.clearSelection();
                return;
            }

            this.setPhotoFile(0, normalizedFile, "Selected image");
            this.updateSubmitButtonState();
            this.updateControlButtons('default');
            this.updateLayoutState(true);
            this.updateReferenceControls();
        } else {
            this.clearSelection();
        }
    }

    secondaryFileSelected = async () => {
        if (this.photoCount() >= 2) {
            this.updateSecondaryUploadButtonState();
            return;
        }

        const file = this.hasSecondaryFileInputTarget ? this.secondaryFileInputTarget.files?.[0] : null;

        if (file && this.validateImage(file)) {
            const normalizedFile = await this.normalizeUploadFile(file);
            if (!normalizedFile) {
                this.clearSecondaryFileInput();
                return;
            }

            this.setPhotoFile(1, normalizedFile, "Second photo");
            this.updateSubmitButtonState();
            this.updateControlButtons('default');
            this.updateLayoutState(true);
            this.updateReferenceControls();
        } else {
            this.clearSecondaryFileInput();
        }
    }

    referenceFileSelected = async (event: Event) => {
        const input = event.currentTarget as HTMLInputElement | null;
        const file = input?.files?.[0];
        if (input) input.value = '';
        if (!file || !this.activePhotoFile()) {
            this.showError('Select the meal photo this reference belongs to first.');
            return;
        }
        if (!this.validateImage(file)) return;
        if (this.referenceFiles.flat().length >= 8) {
            this.showError('You can add up to 8 reference images to one meal.');
            return;
        }
        const foodIndex = this.activePhotoIndex;
        const normalizedFile = await this.normalizeUploadFile(file);
        if (!normalizedFile) return;
        this.referenceFiles[foodIndex].push(normalizedFile);
        this.updateReferenceControls();
    }

    removeReference = (event: Event) => {
        const button = event.currentTarget as HTMLElement | null;
        const foodIndex = Number(button?.dataset.foodIndex);
        const referenceIndex = Number(button?.dataset.referenceIndex);
        if (!Number.isInteger(foodIndex) || !Number.isInteger(referenceIndex)) return;
        this.referenceFiles[foodIndex]?.splice(referenceIndex, 1);
        this.updateReferenceControls();
    }

    // Stimulus action for camera toggle
    toggleCamera = async () => {
        if (this.isCameraMode) {
            this.stopCamera();
        } else {
            await this.startCamera(0);
        }
    }

    startSecondaryCamera = async () => {
        if (this.isCameraMode || !this.selectedFile || this.photoCount() >= 2) {
            this.updateSecondaryUploadButtonState();
            return;
        }

        await this.startCamera(1);
    }

    // Stimulus action for capturing photo
    capturePhoto = (event?: Event) => {
        if (!this.video) {
            return;
        }

        const captureButton = event?.currentTarget instanceof HTMLButtonElement
            ? event.currentTarget
            : null;
        this.setCameraCaptureLoading(captureButton, true);

        this.canvas = document.createElement('canvas');
        const context = this.canvas.getContext('2d');
        
        if (!context) {
            this.setCameraCaptureLoading(captureButton, false);
            return;
        }

        const scaledDimensions = this.scaleDimensions(
            this.video.videoWidth,
            this.video.videoHeight,
            MAX_UPLOAD_IMAGE_DIMENSION,
        );
        this.canvas.width = scaledDimensions.width;
        this.canvas.height = scaledDimensions.height;
        
        context.drawImage(this.video, 0, 0, scaledDimensions.width, scaledDimensions.height);
        
        this.canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
                const photoIndex = this.cameraCapturePhotoIndex;
                this.stopCamera();
                this.setPhotoFile(photoIndex, file, photoIndex === 1 ? "Second photo" : "Captured photo");
                this.updateSubmitButtonState();
                this.updateControlButtons('default');
                this.updateLayoutState(true);
                this.updateReferenceControls();
            } else {
                this.setCameraCaptureLoading(captureButton, false);
                this.showError('Photo capture failed. Please try again.');
            }
        }, 'image/jpeg', UPLOAD_JPEG_QUALITY);
    }

    // Stimulus action for clearing selection
    clearSelection = () => {
        this.selectedFile = null;
        this.secondarySelectedFile = null;
        this.referenceFiles = [[], []];
        this.revokePreviewObjectUrl();
        this.revokeSecondaryPreviewObjectUrl();
        this.activePhotoIndex = 0;
        this.isCropMode = false;
        this.cropInteraction = null;
        this.cropBox = { x: 8, y: 8, width: 84, height: 84 };
        this.stopCamera();
        this.updateUploadContent(this.getDefaultUploadContent(), false);
        this.updateSubmitButtonState();
        this.updateControlButtons('default');
        this.updateLayoutState(false);

        if (this.hasFileInputTarget) {
            this.fileInputTarget.value = '';
        }

        this.clearSecondaryFileInput();
        this.updateSecondaryUploadButtonState();
        this.updateReferenceControls();
    }

    removeActivePhoto = () => {
        const activeIndex = this.activePhotoIndex;

        if (activeIndex === 0) {
            this.referenceFiles[0] = this.referenceFiles[1] || [];
            this.referenceFiles[1] = [];
            this.selectedFile = this.secondarySelectedFile;
            this.revokePreviewObjectUrl();
            this.previewObjectUrl = this.secondaryPreviewObjectUrl;
            this.secondarySelectedFile = null;
            this.secondaryPreviewObjectUrl = null;
            this.activePhotoIndex = 0;
            this.replacePhotoInputFile(0, this.selectedFile);
            this.clearSecondaryFileInput();
        } else {
            this.referenceFiles[1] = [];
            this.secondarySelectedFile = null;
            this.revokeSecondaryPreviewObjectUrl();
            this.activePhotoIndex = 0;
            this.clearSecondaryFileInput();
        }

        this.isCropMode = false;
        this.cropInteraction = null;
        this.cropBox = { x: 8, y: 8, width: 84, height: 84 };

        if (!this.selectedFile) {
            this.clearSelection();
            return;
        }

        this.renderCurrentPhotoPreview("Selected image");
        this.updateSubmitButtonState();
        this.updateControlButtons('default');
        this.updateLayoutState(true);
        this.updateSecondaryUploadButtonState();
        this.updateReferenceControls();
    }

    showPhoto = (event: Event) => {
        const target = event.currentTarget as HTMLElement | null;
        const index = Number(target?.dataset.photoIndex || 0);
        this.setActivePhotoIndex(index);
    }

    showPreviousPhoto = () => {
        this.setActivePhotoIndex(this.activePhotoIndex - 1);
    }

    showNextPhoto = () => {
        this.setActivePhotoIndex(this.activePhotoIndex + 1);
    }

    startPhotoSwipe = (event: PointerEvent) => {
        if (this.isCropMode || this.photoCount() < 2) {
            return;
        }

        this.photoSwipeStartClientX = event.clientX;
    }

    endPhotoSwipe = (event: PointerEvent) => {
        if (this.photoSwipeStartClientX === null) {
            return;
        }

        const deltaX = event.clientX - this.photoSwipeStartClientX;
        this.photoSwipeStartClientX = null;

        if (Math.abs(deltaX) < 42) {
            return;
        }

        if (deltaX < 0) {
            this.showNextPhoto();
        } else {
            this.showPreviousPhoto();
        }
    }

    startCrop = () => {
        if (!this.selectedFile || !this.previewObjectUrl) {
            return;
        }

        this.isCropMode = true;
        this.cropBox = { x: 8, y: 8, width: 84, height: 84 };
        this.renderCurrentPhotoPreview("Selected image");
        this.scheduleCropBoxSync();
    }

    cancelCrop = () => {
        if (!this.selectedFile || !this.previewObjectUrl) {
            return;
        }

        this.isCropMode = false;
        this.cropInteraction = null;
        this.renderCurrentPhotoPreview("Selected image");
    }

    applyCrop = async () => {
        if (!this.selectedFile) {
            return;
        }

        try {
            const activeFile = this.activePhotoFile();
            if (!activeFile) {
                return;
            }

            const croppedFile = await this.cropImageFile(activeFile, this.cropBox);
            this.isCropMode = false;
            this.setPhotoFile(this.activePhotoIndex, croppedFile, "Cropped image");
            this.updateSubmitButtonState();
        } catch (error) {
            console.error('Error cropping image:', error);
            this.showError('The photo could not be cropped on this device. You can still analyse the full photo.');
        }
    }

    startCropMove = (event: PointerEvent) => {
        if (!this.isCropMode) {
            return;
        }

        event.preventDefault();
        const target = event.currentTarget as HTMLElement;
        target.setPointerCapture(event.pointerId);
        this.cropInteraction = {
            mode: 'move',
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startBox: { ...this.cropBox },
        };
    }

    startCropResize = (event: PointerEvent) => {
        if (!this.isCropMode) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        const target = event.currentTarget as HTMLElement;
        target.setPointerCapture(event.pointerId);
        this.cropInteraction = {
            mode: 'resize',
            pointerId: event.pointerId,
            edge: target.dataset.cropEdge || '',
            startClientX: event.clientX,
            startClientY: event.clientY,
            startBox: { ...this.cropBox },
        };
    }

    handleCropPointerMove = (event: PointerEvent) => {
        if (!this.cropInteraction || event.pointerId !== this.cropInteraction.pointerId) {
            return;
        }

        const image = this.cropImageElement();
        if (!image) {
            return;
        }

        const rect = image.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return;
        }

        const dx = ((event.clientX - this.cropInteraction.startClientX) / rect.width) * 100;
        const dy = ((event.clientY - this.cropInteraction.startClientY) / rect.height) * 100;

        if (this.cropInteraction.mode === 'move') {
            this.cropBox = this.normalizeCropBox({
                ...this.cropInteraction.startBox,
                x: this.cropInteraction.startBox.x + dx,
                y: this.cropInteraction.startBox.y + dy,
            });
        } else {
            this.cropBox = this.resizeCropBox(this.cropInteraction.startBox, this.cropInteraction.edge, dx, dy);
        }

        this.syncCropBoxElement();
    }

    handleCropPointerUp = (event: PointerEvent) => {
        if (!this.cropInteraction || event.pointerId !== this.cropInteraction.pointerId) {
            return;
        }

        this.cropInteraction = null;
    }

    // Stimulus action for form submission
    submitForm = async () => {
        // In reanalyse mode, allow submission without a file
        // Otherwise, require a file to be selected
        if (!this.reanalyseModeValue && !this.selectedFile) {
            this.showError('Please select an image before analyzing');
            return false;
        }

        if (!navigator.onLine) {
            this.showError('You appear to be offline. Keep this page open and try again when your connection returns.');
            return false;
        }

        if (this.addingQueueImage) {
            this.submitButtonTarget.disabled = true;
            this.dispatch('queue-image', {
                detail: {
                    primaryFile: this.selectedFile,
                    secondaryFile: this.secondarySelectedFile,
                    additionalInfo: this.hasAdditionalInfoTarget ? this.additionalInfoTarget.value.trim() : '',
                    references: this.referencePayload(),
                },
            });
            return true;
        }

        if (!this.reanalyseModeValue) {
            this.queueActive = true;
            this.showLoadingState();
            this.dispatch('queue', {
                detail: {
                    primaryFile: this.selectedFile,
                    secondaryFile: this.secondarySelectedFile,
                    additionalInfo: this.hasAdditionalInfoTarget ? this.additionalInfoTarget.value.trim() : '',
                    references: this.referencePayload(),
                },
            });
            return true;
        }

        if (this.hasQueueButtonTarget) {
            this.queueButtonTarget.disabled = true;
            this.queueButtonTarget.classList.remove('analysis-submit-fab-visible');
            this.queueButtonTarget.classList.add('analysis-submit-fab-hidden');
            this.queueButtonTarget.setAttribute('aria-hidden', 'true');
        }
        this.showLoadingState();

        if (this.selectedFile) {
            try {
                await this.attachSelectedImagesForSubmit();
            } catch (error) {
                console.error('Error preparing selected image for submission:', error);
                this.hideLoadingState();
                this.updateSubmitButtonState();
                this.showError('The photo could not be prepared for analysis. Please try again.');
                return false;
            }
        }

        updateManualAnalysisProgress(this.element, 'requesting');
        const requestId = this.createAnalysisRequestId();
        this.setAnalysisRequestIdField(requestId);
        this.startAnalysisStatusPolling(requestId);
        this.formTarget.requestSubmit();
    }

    queueFailed = (event: CustomEvent<{ message?: string }>) => {
        this.queueActive = false;
        this.hideLoadingState();
        this.updateSubmitButtonState();
        this.showError(event.detail?.message || 'The photo could not be added to the analysis queue. Please try again.');
    }

    showAddMoreButton = () => {
        if (!this.hasQueueButtonTarget) return;
        this.queueButtonTarget.disabled = false;
        this.queueButtonTarget.classList.remove('analysis-submit-fab-hidden');
        this.queueButtonTarget.classList.add('analysis-submit-fab-visible');
        this.queueButtonTarget.setAttribute('aria-hidden', 'false');
    }

    showQueueWorkspace = () => {
        this.hideLoadingState();
        this.addingQueueImage = true;
        if (this.hasMealTitleTarget) this.mealTitleTarget.textContent = 'Add another item';
        this.clearSelection();
        this.showPhotoMode();
        if (this.hasAdditionalInfoTarget) this.additionalInfoTarget.value = '';
        const submitLabel = this.submitButtonTarget.querySelector('span');
        if (submitLabel) submitLabel.textContent = 'Add image to meal';
        if (this.hasQueueButtonTarget) {
            this.queueButtonTarget.disabled = true;
            this.queueButtonTarget.classList.remove('analysis-submit-fab-visible');
            this.queueButtonTarget.classList.add('analysis-submit-fab-hidden');
            this.queueButtonTarget.setAttribute('aria-hidden', 'true');
        }
    }

    queuedImageAdded = () => {
        if (this.hasAdditionalInfoTarget) this.additionalInfoTarget.value = '';
        this.clearSelection();
        this.showQueuedItemSuccess();
    }

    queuedManualAdded = (event: CustomEvent<{ initial?: boolean }>) => {
        this.queueActive = true;
        this.showPhotoMode();
        if (!event.detail?.initial) this.showQueuedItemSuccess();
    }

    queuedImageFailed = (event: CustomEvent<{ message?: string }>) => {
        this.updateSubmitButtonState();
        this.showError(event.detail?.message || 'The image could not be added to this meal. Please try again.');
    }

    toggleRecording = async (event?: Event) => {
        event?.preventDefault();

        if (this.isTranscribing) {
            return;
        }

        if (this.isRecording) {
            this.mediaRecorder?.stop();
            return;
        }

        if (!this.speechToTextSupported) {
            this.setRecordStatus('Mic unavailable in this browser.');
            this.showError('Speech-to-text is unavailable in this browser. Please type the details instead.');
            return;
        }

        try {
            this.setRecordStatus('Requesting microphone...');
            this.audioChunks = [];
            this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            this.mediaRecorder = new MediaRecorder(this.audioStream);

            this.mediaRecorder.addEventListener('dataavailable', (mediaEvent: BlobEvent) => {
                if (mediaEvent.data.size > 0) {
                    this.audioChunks.push(mediaEvent.data);
                }
            });

            this.mediaRecorder.addEventListener('stop', async () => {
                const mimeType = this.mediaRecorder?.mimeType || this.audioChunks[0]?.type || 'audio/webm';
                const audioBlob = new Blob(this.audioChunks, { type: mimeType });
                this.stopSpeechCapture();
                await this.transcribeAudio(audioBlob);
            });

            this.mediaRecorder.start();
            this.isRecording = true;
            this.updateSpeechToTextUi();
        } catch (error) {
            console.error('Error starting speech capture:', error);
            this.stopSpeechCapture();
            this.setRecordStatus('Microphone blocked.');
            this.showError('Microphone access was blocked. Please allow microphone permission or type the details instead.');
        }
    }

    private validateImage(file: File): boolean {
        if (!file.type.startsWith('image/')) {
            this.showError('Please select a valid image file');
            return false;
        }

        if (file.size > MAX_SOURCE_IMAGE_BYTES) {
            this.showError('This image is too large to prepare on this device. Please choose a smaller photo.');
            return false;
        }

        return true;
    }

    private async normalizeUploadFile(file: File): Promise<File | null> {
        try {
            const normalizedBlob = await this.renderImageFileToJpeg(file);
            const normalizedName = file.name.replace(/\.[^.]+$/, '') || 'upload';
            return new File([normalizedBlob], `${normalizedName}.jpg`, { type: 'image/jpeg' });
        } catch (error) {
            console.error('Error normalizing uploaded image:', error);
            this.showError('This image format is not supported on this device. Please choose a JPEG or PNG image instead.');
            return null;
        }
    }

    private renderImageFileToJpeg(file: File): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            const objectUrl = URL.createObjectURL(file);

            image.onload = () => {
                const canvas = document.createElement('canvas');
                const scaledDimensions = this.scaleDimensions(
                    image.naturalWidth,
                    image.naturalHeight,
                    MAX_UPLOAD_IMAGE_DIMENSION,
                );
                canvas.width = scaledDimensions.width;
                canvas.height = scaledDimensions.height;
                const context = canvas.getContext('2d');

                if (!context) {
                    URL.revokeObjectURL(objectUrl);
                    reject(new Error('Canvas context unavailable'));
                    return;
                }

                context.drawImage(image, 0, 0, scaledDimensions.width, scaledDimensions.height);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(objectUrl);
                    if (!blob) {
                        reject(new Error('JPEG conversion failed'));
                        return;
                    }
                    resolve(blob);
                }, 'image/jpeg', UPLOAD_JPEG_QUALITY);
            };

            image.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Image decode failed'));
            };

            image.src = objectUrl;
        });
    }

    private replacePhotoInputFile(index: number, file: File | null): boolean {
        try {
            if (typeof DataTransfer === 'undefined') {
                return false;
            }

            const dataTransfer = new DataTransfer();
            if (file) {
                dataTransfer.items.add(file);
            }

            const input = index === 0
                ? this.hasFileInputTarget ? this.fileInputTarget : null
                : this.hasSecondaryFileInputTarget ? this.secondaryFileInputTarget : null;

            if (!input) {
                return false;
            }

            input.files = dataTransfer.files;
            return true;
        } catch (error) {
            console.warn('Unable to update file input with processed image; falling back to encoded submit payload.', error);
            return false;
        }
    }

    private scaleDimensions(width: number, height: number, maxDimension: number): { width: number; height: number } {
        if (width <= 0 || height <= 0) {
            return { width: 1, height: 1 };
        }

        const largestDimension = Math.max(width, height);
        if (largestDimension <= maxDimension) {
            return { width, height };
        }

        const scale = maxDimension / largestDimension;
        return {
            width: Math.max(1, Math.round(width * scale)),
            height: Math.max(1, Math.round(height * scale)),
        };
    }

    private photoCount(): number {
        return [this.selectedFile, this.secondarySelectedFile].filter(Boolean).length;
    }

    private photoUrls(): string[] {
        return [this.previewObjectUrl, this.secondaryPreviewObjectUrl].filter((url): url is string => Boolean(url));
    }

    private activePhotoFile(): File | null {
        return this.activePhotoIndex === 1 ? this.secondarySelectedFile : this.selectedFile;
    }

    private setActivePhotoIndex(index: number) {
        const maxIndex = Math.max(this.photoCount() - 1, 0);
        const nextIndex = Math.max(0, Math.min(index, maxIndex));

        if (nextIndex === this.activePhotoIndex && !this.isCropMode) {
            return;
        }

        this.activePhotoIndex = nextIndex;
        this.isCropMode = false;
        this.cropInteraction = null;
        this.cropBox = { x: 8, y: 8, width: 84, height: 84 };
        this.renderCurrentPhotoPreview("Selected image");
        this.updateReferenceControls();
    }

    private updateReferenceControls(): void {
        const enabled = Boolean(this.activePhotoFile()) && (!this.queueActive || this.addingQueueImage);
        this.referenceButtonTargets.forEach((button) => {
            button.classList.toggle('pointer-events-none', !enabled);
            button.classList.toggle('cursor-not-allowed', !enabled);
            button.classList.toggle('cursor-pointer', enabled);
            button.classList.toggle('opacity-55', !enabled);
            button.setAttribute('aria-disabled', String(!enabled));
        });
        if (this.hasReferenceContextTarget) {
            this.referenceContextTarget.textContent = enabled
                ? `New references will apply to meal photo ${this.activePhotoIndex + 1}.`
                : 'Select a meal photo first. References will be linked to the active photo.';
        }
        this.renderReferenceList();
    }

    private renderReferenceList(): void {
        if (!this.hasReferenceListTarget) return;
        this.referenceListTarget.replaceChildren();
        this.referenceFiles.forEach((files, foodIndex) => {
            files.forEach((file, referenceIndex) => {
                const item = document.createElement('li');
                item.className = 'flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2';
                const icon = document.createElement('span');
                icon.className = 'inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700';
                icon.innerHTML = '<i class="fas fa-tag" aria-hidden="true"></i>';
                const label = document.createElement('span');
                label.className = 'min-w-0 flex-1';
                const name = document.createElement('span');
                name.className = 'block truncate text-xs font-semibold text-zinc-900';
                name.textContent = file.name;
                const association = document.createElement('span');
                association.className = 'block text-[11px] text-zinc-500';
                association.textContent = `Reference for meal photo ${foodIndex + 1}`;
                label.append(name, association);
                const remove = document.createElement('button');
                remove.type = 'button';
                remove.className = 'inline-flex size-8 items-center justify-center rounded-full text-zinc-500 hover:bg-white hover:text-red-600';
                remove.setAttribute('aria-label', `Remove ${file.name}`);
                remove.dataset.foodIndex = String(foodIndex);
                remove.dataset.referenceIndex = String(referenceIndex);
                remove.dataset.action = 'click->analysis-form#removeReference';
                remove.innerHTML = '<i class="fas fa-times" aria-hidden="true"></i>';
                item.append(icon, label, remove);
                this.referenceListTarget.append(item);
            });
        });
        this.referenceListTarget.classList.toggle('hidden', this.referenceFiles.flat().length === 0);
    }

    private referencePayload(): Array<{ file: File; foodPosition: number }> {
        return this.referenceFiles.flatMap((files, foodPosition) =>
            files.map((file) => ({ file, foodPosition })),
        );
    }

    private clearSecondaryFileInput() {
        if (this.hasSecondaryFileInputTarget) {
            this.secondaryFileInputTarget.value = '';
            this.replacePhotoInputFile(1, null);
        }
    }

    private updateSecondaryUploadButtonState() {
        const disabled = this.isCameraMode || !this.selectedFile || this.photoCount() >= 2;

        if (this.hasSecondaryUploadButtonTarget) {
            this.secondaryUploadButtonTarget.classList.toggle('pointer-events-none', disabled);
            this.secondaryUploadButtonTarget.classList.toggle('cursor-not-allowed', disabled);
            this.secondaryUploadButtonTarget.classList.toggle('cursor-pointer', !disabled);
            this.secondaryUploadButtonTarget.classList.toggle('opacity-55', disabled);
            this.secondaryUploadButtonTarget.setAttribute('aria-disabled', String(disabled));
        }

        if (this.hasSecondaryCaptureButtonTarget) {
            const captureDisabled = disabled || !this.cameraSupported;
            this.secondaryCaptureButtonTarget.disabled = captureDisabled;
            this.secondaryCaptureButtonTarget.classList.toggle('pointer-events-none', captureDisabled);
            this.secondaryCaptureButtonTarget.classList.toggle('cursor-not-allowed', captureDisabled);
            this.secondaryCaptureButtonTarget.classList.toggle('cursor-pointer', !captureDisabled);
            this.secondaryCaptureButtonTarget.classList.toggle('opacity-55', captureDisabled);
            this.secondaryCaptureButtonTarget.setAttribute('aria-disabled', String(captureDisabled));
        }

        if (this.hasSecondaryFileInputTarget) {
            this.secondaryFileInputTarget.disabled = disabled;
        }
    }

    private renderImagePreviewMarkup(alt: string) {
        const photoUrls = this.photoUrls();
        const photoCount = photoUrls.length;
        const activeIndex = Math.min(this.activePhotoIndex, Math.max(photoCount - 1, 0));
        const cropControls = this.isCropMode
            ? `
                <div class="absolute left-3 top-3 z-20 flex items-center gap-2">
                    <button type="button"
                            class="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/92 px-3 py-2 text-xs font-semibold text-zinc-900 shadow-lg backdrop-blur-sm hover:bg-white"
                            data-action="click->analysis-form#applyCrop">
                        <i class="fas fa-check text-green-700"></i>
                        Apply crop
                    </button>
                    <button type="button"
                            class="inline-flex size-9 items-center justify-center rounded-full border border-white/50 bg-zinc-950/72 text-white shadow-lg backdrop-blur-sm hover:bg-zinc-900"
                            aria-label="Cancel crop"
                            data-action="click->analysis-form#cancelCrop">
                        <i class="fas fa-times text-xs"></i>
                    </button>
                </div>
            `
            : `
                <button type="button"
                        class="absolute left-3 top-3 z-20 inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/90 px-3 py-2 text-xs font-semibold text-zinc-900 shadow-lg backdrop-blur-sm hover:bg-white"
                        data-action="click->analysis-form#startCrop">
                    <i class="fas fa-crop-alt text-green-700"></i>
                    Crop photo
                </button>
            `;
        const photoSlides = photoUrls.map((imageUrl, index) => `
            <div class="min-w-full snap-center">
                <img src="${imageUrl}" alt="${alt} ${index + 1}" class="block w-full h-auto max-h-[70vh] object-contain transition-all duration-300 ${index === activeIndex ? '' : 'pointer-events-none'}" ${index === activeIndex ? 'data-analysis-form-crop-image' : ''}>
            </div>
        `).join('');
        const photoDots = photoCount > 1
            ? `
                <div class="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-zinc-950/50 px-2 py-1 backdrop-blur-sm">
                    ${photoUrls.map((_, index) => `
                        <button type="button"
                                class="size-2 rounded-full transition ${index === activeIndex ? 'bg-white' : 'bg-white/45'}"
                                aria-label="Show photo ${index + 1}"
                                data-photo-index="${index}"
                                data-action="click->analysis-form#showPhoto"></button>
                    `).join('')}
                </div>
            `
            : '';
        const photoNav = photoCount > 1 && !this.isCropMode
            ? `
                <button type="button"
                        class="absolute left-2 top-1/2 z-20 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-zinc-950/55 text-white shadow-lg backdrop-blur-sm transition hover:bg-zinc-900/75 ${activeIndex === 0 ? 'opacity-40' : ''}"
                        aria-label="Previous photo"
                        data-action="click->analysis-form#showPreviousPhoto">
                    <i class="fas fa-chevron-left text-xs"></i>
                </button>
                <button type="button"
                        class="absolute right-2 top-1/2 z-20 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-zinc-950/55 text-white shadow-lg backdrop-blur-sm transition hover:bg-zinc-900/75 ${activeIndex === photoCount - 1 ? 'opacity-40' : ''}"
                        aria-label="Next photo"
                        data-action="click->analysis-form#showNextPhoto">
                    <i class="fas fa-chevron-right text-xs"></i>
                </button>
            `
            : '';

        return `
            <div class="analysis-photo-preview relative flex w-full touch-pan-y justify-center overflow-hidden rounded-xl bg-zinc-950"
                 data-action="pointerdown->analysis-form#startPhotoSwipe pointermove->analysis-form#handleCropPointerMove pointerup->analysis-form#handleCropPointerUp pointerup->analysis-form#endPhotoSwipe pointercancel->analysis-form#handleCropPointerUp pointercancel->analysis-form#endPhotoSwipe">
                <div class="flex w-full transition-transform duration-300 ease-out" style="transform: translateX(-${activeIndex * 100}%);">
                    ${photoSlides}
                </div>
                <div class="analysis-ai-scan-overlay hidden" data-analysis-form-ai-scan-overlay aria-live="polite">
                    <div class="analysis-ai-scan-grid" aria-hidden="true"></div>
                    <div class="analysis-ai-scan-line" aria-hidden="true"></div>
                    <div class="analysis-ai-scan-focus analysis-ai-scan-focus-one" aria-hidden="true"></div>
                    <div class="analysis-ai-scan-focus analysis-ai-scan-focus-two" aria-hidden="true"></div>
                    <div class="analysis-ai-scan-focus analysis-ai-scan-focus-three" aria-hidden="true"></div>

                </div>
                ${this.isCropMode ? this.renderCropBoxMarkup() : ''}
                ${cropControls}
                ${photoNav}
                ${photoDots}
                <button type="button" 
                        class="absolute top-2 right-2 z-30 bg-red-700 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-800 transition-colors" 
                        aria-label="Delete current photo"
                        data-action="click->analysis-form#removeActivePhoto">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>
        `;
    }

    private renderCropBoxMarkup() {
        const handle = (edge: string, classes: string) => `
            <span class="absolute z-20 size-5 touch-none rounded-full border-2 border-white bg-green-600 shadow-md ${classes}"
                  data-crop-edge="${edge}"
                  data-action="pointerdown->analysis-form#startCropResize"></span>
        `;

        return `
            <div class="absolute z-10 touch-none cursor-move border-2 border-white bg-transparent shadow-[0_0_0_9999px_rgba(0,0,0,0.48)]"
                 data-analysis-form-crop-box
                 data-action="pointerdown->analysis-form#startCropMove">
                <span class="pointer-events-none absolute inset-x-1/3 top-0 h-full border-x border-white/55"></span>
                <span class="pointer-events-none absolute inset-y-1/3 left-0 w-full border-y border-white/55"></span>
                ${handle('nw', '-left-2.5 -top-2.5 cursor-nwse-resize')}
                ${handle('ne', '-right-2.5 -top-2.5 cursor-nesw-resize')}
                ${handle('sw', '-bottom-2.5 -left-2.5 cursor-nesw-resize')}
                ${handle('se', '-bottom-2.5 -right-2.5 cursor-nwse-resize')}
            </div>
        `;
    }

    private setPhotoFile(index: number, file: File, alt: string) {
        if (index === 0) {
            this.selectedFile = file;
            this.revokePreviewObjectUrl();
            this.previewObjectUrl = URL.createObjectURL(file);
        } else {
            this.secondarySelectedFile = file;
            this.revokeSecondaryPreviewObjectUrl();
            this.secondaryPreviewObjectUrl = URL.createObjectURL(file);
        }

        this.activePhotoIndex = index;
        this.replacePhotoInputFile(index, file);
        this.cropBox = { x: 8, y: 8, width: 84, height: 84 };
        this.renderCurrentPhotoPreview(alt);
        this.scheduleCropBoxSync();
        this.updateSecondaryUploadButtonState();
    }

    private renderCurrentPhotoPreview(alt: string) {
        this.updateUploadContent(this.renderImagePreviewMarkup(alt), true);
    }

    private async startCamera(photoIndex: 0 | 1 = 0) {
        if (!this.cameraSupported) {
            this.showError('Camera capture is unavailable in this view. Use Upload to continue.');
            return;
        }

        if (photoIndex === 1 && (!this.selectedFile || this.photoCount() >= 2)) {
            this.updateSecondaryUploadButtonState();
            return;
        }

        try {
            this.cameraCapturePhotoIndex = photoIndex;
            this.isCameraMode = true;
            this.updateSubmitButtonState();
            this.updateSecondaryUploadButtonState();
            this.showCameraLoadingState();
            
            this.stream = await navigator.mediaDevices.getUserMedia({ 
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                } ,
                audio: false,
            });
            
            this.setupCameraInterface();
            this.updateCaptureButton('Stop Camera');
            this.updateSubmitButtonState();
            this.updateSecondaryUploadButtonState();
        } catch (error) {
            console.error('Error accessing camera:', error);
            this.isCameraMode = false;
            this.updateSubmitButtonState();
            this.showError(this.mapCameraError(error));
            this.resetUploadContent();
        }
    }

    private setupCameraInterface() {

        this.video = document.createElement('video');

        this.video.autoplay = true;
        this.video.playsInline = true;
        this.video.className = 'block w-full h-auto max-h-[70vh] object-contain';
        
        if (this.stream) {
            this.video.srcObject = this.stream;
            this.video.onloadedmetadata = () => {
                if(this.video) {
                    this.video.play();
                }

            };
        }

        const cameraInterface = `
            <div class="flex w-full justify-center overflow-hidden rounded-xl bg-zinc-950">
                ${this.video.outerHTML}
            </div>
        `;

        this.updateUploadContent(cameraInterface, true);
        this.updateControlButtons('camera');
        
        this.video = this.uploadContentTarget.querySelector('video');
        if (this.video && this.stream) {
            this.video.srcObject = this.stream;
        }
    }

    // Stimulus action for stopping camera
    stopCamera = () => {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
        
        this.video = null;
        this.canvas = null;
        this.isCameraMode = false;
        this.updateSubmitButtonState();
        this.updateCaptureButton('Capture');
        this.updateControlButtons('default');
        
        if (this.selectedFile) {
            this.renderCurrentPhotoPreview("Selected image");
        } else {
            this.updateUploadContent(this.getDefaultUploadContent(), false);
        }

        this.cameraCapturePhotoIndex = 0;
        this.updateSecondaryUploadButtonState();
    }

    private updateUploadContent(content: string, removePadding: boolean = false) {
        const currentHeight = this.uploadContentTarget.offsetHeight;
        
        // Set current height explicitly for smooth transition
        this.uploadContentTarget.style.height = `${currentHeight}px`;
        
        // Update content
        this.uploadContentTarget.innerHTML = content;
        
        // Update padding based on content type
        if (removePadding) {
            this.uploadContentTarget.classList.remove('p-4');
            this.uploadContentTarget.classList.add('p-0');
        } else {
            this.uploadContentTarget.classList.remove('p-4');
            this.uploadContentTarget.classList.add('p-0');
        }
        
        // Force reflow and get new height
        const newHeight = this.uploadContentTarget.scrollHeight;
        
        // Animate to new height
        requestAnimationFrame(() => {
            this.uploadContentTarget.style.height = `${newHeight}px`;
            this.scheduleCropBoxSync();
            
            // Remove explicit height after transition
            setTimeout(() => {
                this.uploadContentTarget.style.height = 'auto';
                this.scheduleCropBoxSync();
            }, 300);
        });
    }

    private showQueuedItemSuccess(): void {
        if (this.queueSuccessTimer !== null) window.clearTimeout(this.queueSuccessTimer);
        this.updateUploadContent(`
            <div data-analysis-form-queue-success
                 class="flex min-h-52 flex-col items-center justify-center gap-3 rounded-2xl bg-green-50 px-6 py-8 text-center"
                 role="status"
                 aria-live="polite">
                <span class="meal-queue-success-icon inline-flex size-20 items-center justify-center rounded-full bg-green-600 text-white shadow-lg shadow-green-200/70">
                    <svg class="size-11" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                        <path class="meal-queue-success-tick" d="M13 24.5L21 32L36 16" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                </span>
                <p class="text-base font-semibold text-green-900">Item added to meal queue</p>
            </div>
        `, false);
        this.queueSuccessTimer = window.setTimeout(() => {
            this.queueSuccessTimer = null;
            if (!this.uploadContentTarget.querySelector('[data-analysis-form-queue-success]')) return;
            this.updateUploadContent(this.getDefaultUploadContent(), false);
        }, 1500);
    }

    private async cropImageFile(file: File, cropBox: CropBox): Promise<File> {
        const image = await this.loadImageFromFile(file);
        const sourceX = Math.round((cropBox.x / 100) * image.naturalWidth);
        const sourceY = Math.round((cropBox.y / 100) * image.naturalHeight);
        const sourceWidth = Math.round((cropBox.width / 100) * image.naturalWidth);
        const sourceHeight = Math.round((cropBox.height / 100) * image.naturalHeight);

        const canvas = document.createElement('canvas');
        const scaledDimensions = this.scaleDimensions(sourceWidth, sourceHeight, MAX_UPLOAD_IMAGE_DIMENSION);
        canvas.width = scaledDimensions.width;
        canvas.height = scaledDimensions.height;
        const context = canvas.getContext('2d');

        if (!context) {
            throw new Error('Canvas context unavailable');
        }

        context.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            scaledDimensions.width,
            scaledDimensions.height,
        );

        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((croppedBlob) => {
                if (!croppedBlob) {
                    reject(new Error('Crop conversion failed'));
                    return;
                }
                resolve(croppedBlob);
            }, 'image/jpeg', UPLOAD_JPEG_QUALITY);
        });

        const fileName = file.name.replace(/\.[^.]+$/, '') || 'meal-photo';
        return new File([blob], `${fileName}-cropped.jpg`, { type: 'image/jpeg' });
    }

    private loadImageFromFile(file: File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            const objectUrl = URL.createObjectURL(file);

            image.onload = () => {
                resolve(image);
                window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
            };

            image.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Image decode failed'));
            };

            image.src = objectUrl;
        });
    }

    private resizeCropBox(startBox: CropBox, edge: string, dx: number, dy: number): CropBox {
        const next = { ...startBox };

        if (edge.includes('w')) {
            next.x = startBox.x + dx;
            next.width = startBox.width - dx;
        }
        if (edge.includes('e')) {
            next.width = startBox.width + dx;
        }
        if (edge.includes('n')) {
            next.y = startBox.y + dy;
            next.height = startBox.height - dy;
        }
        if (edge.includes('s')) {
            next.height = startBox.height + dy;
        }

        return this.normalizeCropBox(next);
    }

    private normalizeCropBox(box: CropBox): CropBox {
        const minSize = 12;
        let width = Math.max(minSize, Math.min(100, box.width));
        let height = Math.max(minSize, Math.min(100, box.height));
        let x = Math.max(0, Math.min(100 - width, box.x));
        let y = Math.max(0, Math.min(100 - height, box.y));

        if (box.x < 0 && box.width !== width) {
            x = 0;
        }
        if (box.y < 0 && box.height !== height) {
            y = 0;
        }

        width = Math.min(width, 100 - x);
        height = Math.min(height, 100 - y);

        return { x, y, width, height };
    }

    private syncCropBoxElement() {
        if (!this.isCropMode) {
            return;
        }

        const image = this.cropImageElement();
        const box = this.cropBoxElement();
        const container = image?.parentElement;
        if (!image || !box || !container) {
            return;
        }

        const imageRect = image.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        box.style.left = `${imageRect.left - containerRect.left + (this.cropBox.x / 100) * imageRect.width}px`;
        box.style.top = `${imageRect.top - containerRect.top + (this.cropBox.y / 100) * imageRect.height}px`;
        box.style.width = `${(this.cropBox.width / 100) * imageRect.width}px`;
        box.style.height = `${(this.cropBox.height / 100) * imageRect.height}px`;
    }

    private scheduleCropBoxSync() {
        requestAnimationFrame(() => {
            const image = this.cropImageElement();
            if (!image) {
                return;
            }

            if (image.complete) {
                this.syncCropBoxElement();
                return;
            }

            image.addEventListener('load', () => this.syncCropBoxElement(), { once: true });
        });
    }

    private cropImageElement(): HTMLImageElement | null {
        return this.uploadContentTarget.querySelector('[data-analysis-form-crop-image]');
    }

    private cropBoxElement(): HTMLElement | null {
        return this.uploadContentTarget.querySelector('[data-analysis-form-crop-box]');
    }

    private revokePreviewObjectUrl() {
        if (this.previewObjectUrl) {
            URL.revokeObjectURL(this.previewObjectUrl);
            this.previewObjectUrl = null;
        }
    }

    private revokeSecondaryPreviewObjectUrl() {
        if (this.secondaryPreviewObjectUrl) {
            URL.revokeObjectURL(this.secondaryPreviewObjectUrl);
            this.secondaryPreviewObjectUrl = null;
        }
    }

    private async attachSelectedImagesForSubmit(): Promise<void> {
        this.formTarget.querySelectorAll('[data-analysis-form-temp-image]').forEach((input) => input.remove());

        this.formTarget.querySelectorAll<HTMLInputElement>('input[type="file"][name="image"], input[type="file"][name="image_secondary"]').forEach((input) => {
            input.disabled = true;
            input.removeAttribute('name');
        });

        const base64Input = document.createElement('input');
        base64Input.type = 'hidden';
        base64Input.name = 'image_base64_upload';
        base64Input.dataset.analysisFormTempImage = 'true';
        base64Input.value = await this.fileToBase64(this.selectedFile as File);
        this.formTarget.appendChild(base64Input);

        if (this.secondarySelectedFile) {
            const secondaryBase64Input = document.createElement('input');
            secondaryBase64Input.type = 'hidden';
            secondaryBase64Input.name = 'secondary_image_base64_upload';
            secondaryBase64Input.dataset.analysisFormTempImage = 'true';
            secondaryBase64Input.value = await this.fileToBase64(this.secondarySelectedFile);
            this.formTarget.appendChild(secondaryBase64Input);
        }
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = String(reader.result || '');
                resolve(result.includes(',') ? result.split(',', 2)[1] : result);
            };
            reader.onerror = () => reject(reader.error || new Error('File read failed'));
            reader.readAsDataURL(file);
        });
    }

    private resetUploadContent() {
        this.uploadContentTarget.innerHTML = this.getDefaultUploadContent();
    }

    private async transcribeAudio(audioBlob: Blob): Promise<void> {
        if (audioBlob.size === 0) {
            this.showError('No speech was captured. Please try again.');
            return;
        }

        this.isTranscribing = true;
        this.updateSpeechToTextUi();

        try {
            const extension = this.mimeTypeToFileExtension(audioBlob.type);
            const audioFile = new File([audioBlob], `meal-details.${extension}`, { type: audioBlob.type || 'audio/webm' });
            const formData = new FormData();
            formData.append('audio', audioFile);

            const response = await fetch('/analyse/transcribe-details', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    ...csrfHeaders(),
                },
                body: formData,
            });

            const data = await response.json().catch(() => null);
            if (!response.ok || !data?.success || typeof data.transcript !== 'string') {
                throw new Error(data?.message || 'Speech transcription failed. Please type the details instead.');
            }

            this.appendTranscript(data.transcript);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Speech transcription failed. Please type the details instead.';
            this.showError(message);
            this.setRecordStatus(message);
        } finally {
            this.isTranscribing = false;
            this.updateSpeechToTextUi();
        }
    }

    private appendTranscript(transcript: string) {
        if (!this.hasAdditionalInfoTarget) {
            return;
        }

        const safeTranscript = transcript.trim();
        if (!safeTranscript) {
            return;
        }

        const existingText = this.additionalInfoTarget.value.trim();
        this.additionalInfoTarget.value = existingText ? `${existingText} ${safeTranscript}` : safeTranscript;
        this.additionalInfoTarget.dispatchEvent(new Event('input', { bubbles: true }));
        this.setRecordStatus('Details added. Review the text and edit anything that needs tweaking.');
        this.additionalInfoTarget.focus();
    }

    private stopSpeechCapture() {
        if (this.mediaRecorder) {
            this.mediaRecorder.ondataavailable = null;
            this.mediaRecorder.onstop = null;
            this.mediaRecorder = null;
        }

        if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
        }

        this.isRecording = false;
        this.audioChunks = [];
        this.updateSpeechToTextUi();
    }

    private updateSpeechToTextUi() {
        if (this.hasRecordButtonTarget) {
            this.recordButtonTarget.disabled = this.isTranscribing;
            this.recordButtonTarget.classList.toggle('opacity-60', this.isTranscribing);
            this.recordButtonTarget.classList.toggle('cursor-not-allowed', this.isTranscribing);
            this.recordButtonTarget.classList.remove('bg-red-50', 'text-red-600', 'bg-zinc-100', 'text-zinc-600');
            this.recordButtonTarget.classList.add('text-zinc-400');

            if (!this.speechToTextSupported) {
                this.recordButtonTarget.innerHTML = `
                    <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M8.25 5.25a3.75 3.75 0 117.5 0v6.75a3.75 3.75 0 11-7.5 0V5.25z" />
                        <path d="M6 11.25a.75.75 0 011.5 0 4.5 4.5 0 109 0 .75.75 0 011.5 0 6.001 6.001 0 01-5.25 5.955V19.5H15a.75.75 0 010 1.5H9a.75.75 0 010-1.5h2.25v-2.295A6.001 6.001 0 016 11.25z" />
                    </svg>
                `;
            } else if (this.isTranscribing) {
                this.recordButtonTarget.classList.remove('text-zinc-400');
                this.recordButtonTarget.classList.add('text-zinc-600');
                this.recordButtonTarget.innerHTML = `
                    <svg class="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                `;
            } else if (this.isRecording) {
                this.recordButtonTarget.classList.remove('text-zinc-400');
                this.recordButtonTarget.classList.add('text-red-600');
                this.recordButtonTarget.innerHTML = `
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <rect x="4" y="4" width="12" height="12" rx="1.5"></rect>
                    </svg>
                `;
            } else {
                this.recordButtonTarget.innerHTML = `
                    <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M8.25 5.25a3.75 3.75 0 117.5 0v6.75a3.75 3.75 0 11-7.5 0V5.25z" />
                        <path d="M6 11.25a.75.75 0 011.5 0 4.5 4.5 0 109 0 .75.75 0 011.5 0 6.001 6.001 0 01-5.25 5.955V19.5H15a.75.75 0 010 1.5H9a.75.75 0 010-1.5h2.25v-2.295A6.001 6.001 0 016 11.25z" />
                    </svg>
                `;
            }
        }

        if (this.isRecording) {
            this.setRecordStatus('Recording...');
            return;
        }

        if (this.isTranscribing) {
            this.setRecordStatus('Transcribing...');
            return;
        }

        if (!this.speechToTextSupported) {
            this.setRecordStatus('');
            return;
        }

        this.setRecordStatus('');
    }

    private setRecordStatus(message: string) {
        if (!this.hasRecordStatusTarget) {
            return;
        }

        this.recordStatusTarget.textContent = message;
    }

    private getDefaultUploadContent(): string {
        if (this.defaultUploadContent) return this.defaultUploadContent;
        const cameraNote = this.cameraSupported
            ? ''
            : `
                <p class="mt-2 text-xs text-amber-700">
                    Camera capture is not available here. Please upload an image from your device.
                </p>
            `;

        return `
            <div class="flex flex-col items-center justify-center text-center">
                <div class="relative w-full overflow-hidden rounded-lg bg-white" style="aspect-ratio: 3 / 1.65;" aria-label="Meal photo guidance">
                    <img src="/static/images/photo-tip-hand.png" alt="Include cutlery or a hand for size reference" class="photo-tip-frame-primary absolute inset-0 h-full w-full object-contain">
                    <img src="/static/images/photo-tip-angle.png" alt="Take the photo at an angle to show height, width and depth" class="photo-tip-frame-secondary absolute inset-0 h-full w-full object-contain">
                </div>
                <div class="analysis-photo-guidance-caption mt-2 grid min-h-8 w-full place-items-center rounded-2xl border border-green-100 bg-green-50 px-3 py-1 text-center shadow-xs">
                    <p class="photo-tip-caption-primary col-start-1 row-start-1 flex items-center justify-center gap-1 text-xs font-semibold leading-5 text-zinc-800 sm:text-sm">
                        <span class="text-emerald-700">Include a hand or cutlery</span><span>to show scale.</span>
                    </p>
                    <p class="photo-tip-caption-secondary col-start-1 row-start-1 flex items-center justify-center gap-1 text-xs font-semibold leading-5 text-zinc-800 sm:text-sm">
                        <span class="text-emerald-700">Add an angled photo</span><span>to show depth.</span>
                    </p>
                </div>
                ${cameraNote}
            </div>
        `;
    }

    private updateCaptureButton(text: string) {
        if (this.hasCaptureButtonTarget) {
            const icon = this.captureButtonTarget.querySelector('i');
            this.captureButtonTarget.innerHTML = `${icon?.outerHTML || '<i class="fas fa-camera text-lg"></i>'} ${text}`;
        }
    }

    private updateSubmitButtonState() {
        if (!this.hasSubmitButtonTarget) {
            return;
        }

        const canSubmit = (this.reanalyseModeValue || !!this.selectedFile)
            && !this.isCameraMode
            && (!this.queueActive || this.addingQueueImage);
        this.submitButtonTarget.disabled = !canSubmit;
        this.submitButtonTarget.classList.toggle('hidden', this.isCameraMode);

        if (this.reanalyseModeValue) {
            return;
        }

        this.submitButtonTarget.classList.toggle('analysis-submit-fab-visible', canSubmit);
        this.submitButtonTarget.classList.toggle('analysis-submit-fab-hidden', !canSubmit);
        this.submitButtonTarget.setAttribute('aria-hidden', canSubmit ? 'false' : 'true');
        if (this.hasQueueButtonTarget && !this.queueActive) {
            this.queueButtonTarget.disabled = true;
            this.queueButtonTarget.classList.remove('analysis-submit-fab-visible');
            this.queueButtonTarget.classList.add('analysis-submit-fab-hidden');
            this.queueButtonTarget.setAttribute('aria-hidden', 'true');
        }
    }

    private updateLayoutState(hasImage: boolean) {
        if (this.hasControlButtonsTarget) {
            const manualEntryOpen = this.hasManualPanelTarget && !this.manualPanelTarget.classList.contains('hidden');
            this.controlButtonsTarget.classList.toggle('hidden', hasImage || manualEntryOpen);
        }

        if (!this.hasUploadSectionTarget || !this.hasDetailsPanelTarget) {
            return;
        }

        if (hasImage) {
            this.detailsPanelTarget.classList.remove('hidden');
            this.formTarget.classList.remove('space-y-6');
            this.formTarget.classList.add('grid', 'grid-cols-1', 'lg:grid-cols-5', 'gap-6', 'items-start');
            this.uploadSectionTarget.classList.add('lg:col-span-3');
            this.detailsPanelTarget.classList.add('lg:col-span-2', 'lg:sticky', 'lg:top-6');

            if (this.hasDetailsCardTarget) {
                this.detailsCardTarget.classList.add('ring-1', 'ring-emerald-100', 'shadow-md');
            }
        } else {
            this.detailsPanelTarget.classList.add('hidden');
            this.formTarget.classList.remove('grid', 'grid-cols-1', 'lg:grid-cols-5', 'gap-6', 'items-start');
            this.formTarget.classList.add('space-y-6');
            this.uploadSectionTarget.classList.remove('lg:col-span-3');
            this.detailsPanelTarget.classList.remove('lg:col-span-2', 'lg:sticky', 'lg:top-6');

            if (this.hasDetailsCardTarget) {
                this.detailsCardTarget.classList.remove('ring-1', 'ring-emerald-100', 'shadow-md');
            }
        }
    }

    private showError(message: string) {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 transition-all duration-300';
        errorDiv.textContent = message;
        
        this.formTarget.insertBefore(errorDiv, this.formTarget.firstChild);
        
        setTimeout(() => {
            errorDiv.style.opacity = '0';
            setTimeout(() => errorDiv.remove(), 300);
        }, 5000);
    }

    private showLoadingState() {
        this.submitButtonTarget.disabled = true;
        if (this.hasControlButtonsTarget) this.controlButtonsTarget.classList.add('hidden');
        this.showAiScanOverlay();
        this.submitButtonTarget.innerHTML = `
            <svg class="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Analysing...</span>
        `;
    }

    private hideLoadingState() {
        this.submitButtonTarget.disabled = false;
        if (this.hasControlButtonsTarget
            && !this.selectedFile
            && (!this.hasManualPanelTarget || this.manualPanelTarget.classList.contains('hidden'))) {
            this.controlButtonsTarget.classList.remove('hidden');
        }
        this.hideAiScanOverlay();
        this.submitButtonTarget.innerHTML = '<span>Analyse</span>';
        this.updateSubmitButtonState();
    }

    private createAnalysisRequestId(): string {
        if (typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
    }

    private setAnalysisRequestIdField(requestId: string): void {
        let input = this.formTarget.querySelector<HTMLInputElement>(
            'input[name="analysis_request_id"]',
        );
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'analysis_request_id';
            this.formTarget.appendChild(input);
        }
        input.value = requestId;
    }

    private showAiScanOverlay() {
        const overlay = this.uploadContentTarget.querySelector<HTMLElement>('[data-analysis-form-ai-scan-overlay]');
        if (!overlay) {
            return;
        }

        overlay.classList.remove('hidden');
        overlay.classList.add('is-active');
        updateManualAnalysisProgress(this.element, 'queued');
        this.uploadContentTarget.querySelectorAll<HTMLElement>('[data-action]').forEach((element) => {
            element.classList.add('pointer-events-none');
        });
    }

    private hideAiScanOverlay() {
        this.stopAnalysisStatusPolling();
        const overlay = this.uploadContentTarget.querySelector<HTMLElement>('[data-analysis-form-ai-scan-overlay]');
        if (!overlay) {
            return;
        }

        overlay.classList.remove('is-active');
        overlay.classList.add('hidden');
        this.uploadContentTarget.querySelectorAll<HTMLElement>('[data-action]').forEach((element) => {
            element.classList.remove('pointer-events-none');
        });
    }

    private startAnalysisStatusPolling(requestId: string) {
        this.stopAnalysisStatusPolling();
        this.activeAnalysisRequestId = requestId;
        this.scheduleAnalysisStatusPoll(requestId, 250);
    }

    private scheduleAnalysisStatusPoll(requestId: string, delay = ANALYSIS_STATUS_POLL_INTERVAL_MS) {
        this.analysisStatusPollTimer = window.setTimeout(() => {
            void this.pollAnalysisStatus(requestId);
        }, delay);
    }

    private async pollAnalysisStatus(requestId: string) {
        this.analysisStatusAbortController = new AbortController();
        try {
            const response = await fetch(
                `/analyse/status?request_id=${encodeURIComponent(requestId)}`,
                {
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'application/json',
                        ...csrfHeaders(),
                    },
                    signal: this.analysisStatusAbortController.signal,
                },
            );
            const payload = await response.json().catch(() => null) as { status?: string; message?: string } | null;
            if (response.ok && payload?.status && this.activeAnalysisRequestId === requestId) {
                updateManualAnalysisProgress(this.element, payload.status, payload.message);
            }
        } catch (error) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
                console.debug('Analysis progress is temporarily unavailable.');
            }
        } finally {
            this.analysisStatusAbortController = null;
        }

        if (this.activeAnalysisRequestId === requestId && this.submitButtonTarget.disabled) {
            this.scheduleAnalysisStatusPoll(requestId);
        }
    }

    private stopAnalysisStatusPolling() {
        this.activeAnalysisRequestId = null;
        if (this.analysisStatusPollTimer !== null) {
            window.clearTimeout(this.analysisStatusPollTimer);
            this.analysisStatusPollTimer = null;
        }
        this.analysisStatusAbortController?.abort();
        this.analysisStatusAbortController = null;
    }

    private handleAnalysisSubmitEnd = (event: Event) => {
        const submitEvent = event as CustomEvent<{ success?: boolean }>;
        if (submitEvent.detail?.success) {
            this.stopAnalysisStatusPolling();
            updateManualAnalysisProgress(this.element, 'ready');
            return;
        }
        updateManualAnalysisProgress(this.element, 'failed');
        this.hideLoadingState();
    };

    private showCameraLoadingState() {
        this.updateUploadContent(`
            <div class="flex flex-col items-center justify-center py-12 space-y-4">
                <svg class="animate-spin size-8 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="text-sm text-zinc-600">Activating camera...</p>
                <p class="text-xs text-zinc-500">Please allow camera permissions if prompted</p>
            </div>
        `);
    }

    private setCameraCaptureLoading(button: HTMLButtonElement | null, isLoading: boolean) {
        if (!button) {
            return;
        }

        button.disabled = isLoading;
        button.classList.toggle('opacity-80', isLoading);
        button.classList.toggle('cursor-wait', isLoading);
        button.innerHTML = isLoading
            ? `
                <svg class="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Hold still...</span>
            `
            : '<i class="fas fa-camera text-lg"></i> Take Photo';
    }

    private updateControlButtons(mode: 'default' | 'camera') {
        if (!this.hasControlButtonsTarget) {return;}

        if (mode === 'camera') {
            if (this.hasSubmitButtonTarget) {
                this.submitButtonTarget.classList.add('hidden');
            }
            this.controlButtonsTarget.className = 'fixed bottom-[7.5rem] right-5 z-30 flex flex-row items-center gap-3 sm:static sm:bottom-auto sm:right-auto sm:z-auto sm:flex-row';
            this.controlButtonsTarget.innerHTML = `
                <div class="sm:flex sm:w-0 sm:flex-1 sm:border-r sm:border-zinc-200">
                    <button type="button" 
                            data-action="click->analysis-form#capturePhoto"
                            class="analysis-floating-control analysis-floating-control--primary inline-flex items-center justify-center gap-x-3 rounded-full bg-green-600 px-5 py-4 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-green-700 sm:-mr-px sm:w-0 sm:flex-1 sm:rounded-bl-2xl sm:rounded-tr-none sm:border sm:border-transparent sm:px-0 sm:py-3.5 sm:shadow-none">
                        <i class="fas fa-camera text-lg"></i> Take Photo
                    </button>
                </div>
                <div class="order-first sm:order-none sm:-ml-px sm:flex sm:w-0 sm:flex-1">
                    <button type="button" 
                            data-action="click->analysis-form#stopCamera"
                            class="analysis-floating-control analysis-floating-control--secondary inline-flex items-center justify-center gap-x-2 rounded-full bg-zinc-800 px-4 py-4 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-zinc-700 sm:h-auto sm:w-0 sm:flex-1 sm:rounded-br-2xl sm:rounded-tl-none sm:border sm:border-transparent sm:px-0 sm:py-3.5 sm:shadow-none">
                        <i class="fas fa-times text-lg"></i>
                        <span>Cancel</span>
                    </button>
                </div>
            `;
        } else {
            if (this.hasSubmitButtonTarget) {
                this.submitButtonTarget.classList.remove('hidden');
            }
            this.controlButtonsTarget.className = 'flex';
            const uploadDisabled = this.selectedFile !== null;
            const captureDisabled = uploadDisabled || !this.cameraSupported;
            const captureClasses = captureDisabled ? 'analysis-control-button--disabled bg-zinc-300 text-zinc-500 cursor-not-allowed' : 'analysis-control-button--primary bg-green-600 text-white hover:bg-green-700';
            const uploadClasses = uploadDisabled ? 'analysis-control-button--disabled bg-zinc-300 text-zinc-500 cursor-not-allowed' : 'analysis-control-button--primary bg-green-600 text-white hover:bg-green-700';
            const capturePointerEvents = captureDisabled ? 'pointer-events-none' : '';
            const uploadPointerEvents = uploadDisabled ? 'pointer-events-none' : '';
            const captureIcon = this.cameraSupported ? 'fas fa-camera text-lg' : 'fas fa-camera-slash text-lg';
            const captureLabel = this.cameraSupported ? 'Capture' : 'Camera Unavailable';
            this.controlButtonsTarget.innerHTML = `
                <div class="flex w-full">
                <div class="flex w-0 flex-1 border-r border-zinc-200">
                    <button type="button" 
                            data-analysis-form-target="captureButton"
                            data-action="click->analysis-form#toggleCamera"
                            ${captureDisabled ? 'disabled' : ''}
                            class="${captureClasses} relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-2xl border border-transparent py-3.5 text-sm font-semibold transition-colors ${capturePointerEvents}">
                        <i class="${captureIcon}"></i> ${captureLabel}
                    </button>
                </div>
                <div class="-ml-px flex w-0 flex-1">
                    <label for="galleryInput" class="${uploadClasses} relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-2xl border border-transparent py-3.5 text-sm font-semibold transition-colors ${uploadPointerEvents}">
                        <i class="fas fa-cloud-upload-alt text-lg"></i> Upload
                        <input type="file" 
                               id="galleryInput" 
                               name="image" 
                               accept="image/jpeg,image/png,image/webp,image/heic,image/heif" 
                               data-analysis-form-target="fileInput"
                               data-action="change->analysis-form#fileSelected"
                               ${uploadDisabled ? 'disabled' : ''}
                               class="hidden">
                    </label>
                </div>
                </div>
            `;
        }
    }

    private isCameraSupported(): boolean {
        return Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }

    private isSpeechToTextSupported(): boolean {
        return Boolean(window.MediaRecorder && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }

    private mimeTypeToFileExtension(mimeType: string): string {
        const normalizedType = mimeType.split(';', 1)[0].trim().toLowerCase();
        switch (normalizedType) {
            case 'audio/mp4':
            case 'audio/x-m4a':
            case 'audio/m4a':
                return 'm4a';
            case 'audio/mpeg':
                return 'mp3';
            case 'audio/wav':
            case 'audio/x-wav':
                return 'wav';
            case 'audio/ogg':
                return 'ogg';
            default:
                return 'webm';
        }
    }

    private mapCameraError(error: unknown): string {
        const domError = error as DOMException;
        switch (domError?.name) {
            case 'NotAllowedError':
            case 'PermissionDeniedError':
                return 'Camera permission was denied. You can upload a photo instead.';
            case 'NotFoundError':
            case 'DevicesNotFoundError':
                return 'No camera was found on this device. Please upload a photo.';
            case 'NotReadableError':
                return 'The camera is currently in use by another app. Try uploading instead.';
            case 'OverconstrainedError':
                return 'Camera settings were not supported. Please upload a photo instead.';
            default:
                return 'Unable to access camera. Please upload a photo instead.';
        }
    }
}
